import type { AuthorizedResourceAccessMap } from '@/permissions/adapter';
import {
  PERMISSION_ACTION_CODES,
  PERMISSION_RESOURCE_CODES,
} from '@/permissions/definitions';
import { hasResourceAccess } from '@/permissions/helpers';

const buildAdminAccessMap = (
  authorizedResourceAccess: AuthorizedResourceAccessMap = {},
): AuthorizedResourceAccessMap => {
  const adminAccessMap: AuthorizedResourceAccessMap = {
    ...authorizedResourceAccess,
  };

  Object.values(PERMISSION_RESOURCE_CODES).forEach((resourceCode) => {
    adminAccessMap[resourceCode] = true;
  });

  Object.entries(PERMISSION_ACTION_CODES).forEach(
    ([resourceKey, resourceActions]) => {
      const resourceCode =
        PERMISSION_RESOURCE_CODES[
          resourceKey as keyof typeof PERMISSION_RESOURCE_CODES
        ];

      Object.values(resourceActions).forEach((actionCode) => {
        adminAccessMap[`${resourceCode}:${actionCode}`] = true;
      });
    },
  );

  return adminAccessMap;
};

/**
 * @see https://umijs.org/docs/max/access#access
 * */
export default function access(
  initialState:
    | {
        authorizedResourceAccess?: AuthorizedResourceAccessMap;
        currentTenantIsAdmin?: boolean;
      }
    | undefined,
) {
  const { authorizedResourceAccess, currentTenantIsAdmin } = initialState ?? {};

  const accessMap = currentTenantIsAdmin
    ? buildAdminAccessMap(authorizedResourceAccess)
    : authorizedResourceAccess || {};

  return {
    ...accessMap,
    workOrderWorkbench: hasResourceAccess(
      accessMap,
      PERMISSION_RESOURCE_CODES.workbench,
    ),
    workOrderTodo: hasResourceAccess(accessMap, PERMISSION_RESOURCE_CODES.todo),
    myWorkOrders: hasResourceAccess(
      accessMap,
      PERMISSION_RESOURCE_CODES.myWorkOrders,
    ),
    workOrders: hasResourceAccess(
      accessMap,
      PERMISSION_RESOURCE_CODES.workOrders,
    ),
    workOrderTemplates: hasResourceAccess(
      accessMap,
      PERMISSION_RESOURCE_CODES.templates,
    ),
    workOrderStatistics: hasResourceAccess(
      accessMap,
      PERMISSION_RESOURCE_CODES.statistics,
    ),
    workOrderDetail: hasResourceAccess(
      accessMap,
      PERMISSION_RESOURCE_CODES.workOrderDetail,
    ),
    settings: hasResourceAccess(accessMap, PERMISSION_RESOURCE_CODES.settings),
  };
}
